/**
 * Pure pagination math for {@link ./TablePagination}.
 *
 * Extracted verbatim from the component so the windowing/boundary rules are
 * testable without a DOM. Behavior must stay identical — TablePagination is the
 * only intended consumer.
 */

export interface PaginationInfo {
  /** "external" = totals come from the server (`propTotalItems`); "internal" = derived from the loaded rows. */
  type: "external" | "internal";
  /** 1-based index of the first row shown on the current page. */
  start: number;
  /** 1-based index of the last row shown on the current page. */
  end: number;
  total: number;
}

export interface PaginationInfoArgs {
  /** Server-reported total row count, when the caller has one. */
  propTotalItems?: number;
  /** 0-based current page. */
  currentPage: number;
  pageSize: number;
  /** Row count the client currently holds (used when `propTotalItems` is absent). */
  processedCount: number;
}

/** "Showing X–Y of Z" range for the current page. */
export function computePaginationInfo({
  propTotalItems,
  currentPage,
  pageSize,
  processedCount,
}: PaginationInfoArgs): PaginationInfo {
  if (propTotalItems !== undefined) {
    return {
      type: "external",
      start: currentPage * pageSize + 1,
      end: Math.min((currentPage + 1) * pageSize, propTotalItems),
      total: propTotalItems,
    };
  }

  return {
    type: "internal",
    start: currentPage * pageSize + 1,
    end: Math.min((currentPage + 1) * pageSize, processedCount),
    total: processedCount,
  };
}

/** Page the Previous button navigates to. Never goes below the first page. */
export function resolvePreviousPage(currentPage: number): number {
  return Math.max(0, currentPage - 1);
}

/**
 * Page the Next button navigates to.
 *
 * Cursor-based (external) pagination hands the raw next page to the parent
 * handler — the parent owns the cursor and knows whether another page exists.
 * Client-side pagination clamps to the last page.
 */
export function resolveNextPage(
  currentPage: number,
  totalPages: number,
  isExternallyControlled: boolean
): number {
  return isExternallyControlled ? currentPage + 1 : Math.min(totalPages - 1, currentPage + 1);
}

export function isPreviousDisabled(currentPage: number): boolean {
  return currentPage === 0;
}

export function isNextDisabled(currentPage: number, totalPages: number): boolean {
  return currentPage === totalPages - 1;
}

export interface ShouldRenderPaginationArgs {
  infiniteScrollEnabled?: boolean;
  totalPages: number;
  propTotalItems?: number;
}

/**
 * Whether the pager chrome renders at all. Infinite scroll owns its own
 * "load more" affordance, and a single client-side page needs no pager — but a
 * server-reported total always renders so the row count stays visible.
 */
export function shouldRenderPagination({
  infiniteScrollEnabled,
  totalPages,
  propTotalItems,
}: ShouldRenderPaginationArgs): boolean {
  if (infiniteScrollEnabled) {
    return false;
  }
  if (totalPages <= 1 && propTotalItems === undefined) {
    return false;
  }
  return true;
}
